/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperBasketsV2, ShopperCustomers } from '@/scapi';
import type { AddressBookItem } from '@/lib/customer/profile-utils';
import { normalizeUsPhone } from '@/lib/address/phone-utils';

/**
 * Normalizes address field values for comparison
 */
const normalize = (value: string | undefined | null) => (!value ? '' : value);

/**
 * Passthrough hook for US postal-code normalization on SCAPI address writes.
 *
 * Historical context: PR #306 (`feat(order-parity): slice 1`) introduced a
 * `NNNNN-NNNN` → `NNNNN` truncation here to make the persisted SFCC address
 * match the 5-digit ZIP shape in the legacy Ashley SFRA sample order XML
 * (`tmp/725990214048-ck.xml`). The change was cosmetic legacy-XML parity,
 * NOT a hard requirement from any downstream integration (Experian returns
 * ZIP+4; Avalara/Kount/Synchrony all accept 9-digit; order-export XML
 * consumers were never confirmed to require 5-digit). Roland later confirmed
 * the truncation was unintentional for the shopper-facing flow — QA had
 * flagged that "the value saved in ZIP is only 5 digits" as a regression.
 *
 * Current behavior: passthrough. The Experian-verified ZIP+4 flows through
 * unchanged all the way to the persisted `basket.shipments[].shippingAddress.
 * postalCode` / `CustomerAddress.postalCode`.
 *
 * The helper is intentionally retained (rather than deleted) as a hook. If a
 * specific 3rd-party integration is later shown to REQUIRE 5-digit (e.g.
 * legacy order-export XML consumer), the normalization can be reintroduced
 * either here (blanket) or — preferably — inside that integration's payload
 * builder (scoped). Restoring the previous behavior is a one-line diff.
 *
 * `undefined` / `null` / empty input is passed through untouched.
 *
 * @example
 * normalizeUsPostalCode('45420-1469') // '45420-1469' (preserved)
 * normalizeUsPostalCode('45420')      // '45420'
 * normalizeUsPostalCode('M5V 2T6')    // 'M5V 2T6' (Canadian passthrough)
 * normalizeUsPostalCode('')           // ''
 * normalizeUsPostalCode(undefined)    // undefined
 */
export const normalizeUsPostalCode = <T extends string | undefined | null>(postalCode: T): T => postalCode;

/**
 * Normalizes the `phone` field on an OrderAddress / CustomerAddress body so
 * the value PERSISTED to SFCC matches legacy SFRA (`NNN-NNN-NNNN`, not the
 * presentation form `+1 (NNN) NNN-NNNN`).
 *
 * `postalCode` runs through `normalizeUsPostalCode`, which is currently a
 * passthrough — Experian-verified ZIP+4 is preserved end-to-end (see that
 * helper's JSDoc for the design decision). The call is retained here as a
 * hook in case a specific integration later requires 5-digit truncation.
 *
 * ONLY call this immediately before an SCAPI address write
 * (`updateShippingAddressForShipment`, `updateBillingAddressForBasket`,
 * `createCustomerAddress`, `updateCustomerAddress`). NEVER call it on read —
 * the display components (`formatPhoneCompact`, address cards) handle
 * presentation formatting from the persisted value on demand.
 *
 * Non-US addresses (`countryCode !== 'US'`) fall through unchanged: `phone`
 * is only reformatted when it's a recognizable 10-/11-digit US number, and
 * `postalCode` is now a passthrough for every input.
 *
 * @param address - OrderAddress / CustomerAddress body ready for SCAPI. May be
 *   partial (only `phone` and/or `postalCode` present); every other field is
 *   preserved verbatim.
 * @returns A new object with normalized `phone`; `postalCode` is preserved.
 */
export function normalizeAddressForPersistence<T extends { phone?: string | null; postalCode?: string | null }>(
    address: T
): T {
    return {
        ...address,
        phone: normalizeUsPhone(address.phone),
        postalCode: normalizeUsPostalCode(address.postalCode),
    };
}

/**
 * Standard SCAPI `CustomerAddress` field names accepted by the shopper-customers
 * `updateCustomerAddress` (PATCH) and `createCustomerAddress` (POST) endpoints.
 *
 * SCAPI strict-decodes the request body and rejects ANY unknown standard property
 * with `HTTP 400: "unknown property 'foo' in document 'CustomerAddress'"`. In
 * particular, `smsConsent` — which lives on the checkout `ShippingAddressData`
 * because the SMS opt-in checkbox is captured on the shipping-address step — is
 * NOT a CustomerAddress field. Spreading a `ShippingAddressData` (or the
 * server-echoed submitted address) directly into the PATCH body was silently
 * breaking every SavedAddressesList → Continue → Accept flow: the basket update
 * succeeded, then the profile PATCH 400'd and the shopper's saved record was
 * left stale on the bad-ZIP content.
 *
 * `c_*` custom attributes (`c_experianBypass`, `c_experianValidatedAddressHash`)
 * are chain-friendly with SCAPI hooks and are NOT filtered by this helper — call
 * sites attach them explicitly after the whitelist is applied.
 */
const CUSTOMER_ADDRESS_STANDARD_FIELDS = [
    'addressId',
    'address1',
    'address2',
    'city',
    'companyName',
    'countryCode',
    'firstName',
    'fullName',
    'jobTitle',
    'lastName',
    'phone',
    'postBox',
    'postalCode',
    'preferred',
    'salutation',
    'secondName',
    'stateCode',
    'suffix',
    'suite',
    'title',
] as const;

/**
 * Whitelist a candidate object down to the SCAPI-valid `CustomerAddress` fields
 * so it can be safely used as the body of `updateCustomerAddress` /
 * `createCustomerAddress`. Preserves any `c_*` custom attributes verbatim
 * (SCAPI accepts extension attributes) and drops every other unknown field —
 * most importantly `smsConsent` (see `CUSTOMER_ADDRESS_STANDARD_FIELDS`).
 *
 * @param source - any object that may contain a superset of CustomerAddress fields
 * @returns a new object containing only SCAPI-valid CustomerAddress fields + `c_*`
 */
export function toCustomerAddressBody(source: Record<string, unknown>): ShopperCustomers.schemas['CustomerAddress'] {
    const body: Record<string, unknown> = {};
    for (const key of CUSTOMER_ADDRESS_STANDARD_FIELDS) {
        if (source[key] !== undefined) {
            body[key] = source[key];
        }
    }
    for (const key of Object.keys(source)) {
        if (key.startsWith('c_') && source[key] !== undefined) {
            body[key] = source[key];
        }
    }
    return body as ShopperCustomers.schemas['CustomerAddress'];
}

/**
 * Creates a normalized string key from an address for deduplication and comparison.
 * @param address - OrderAddress or CustomerAddress to create a key for
 * @returns A hyphen-separated string of normalized core address fields
 */
export function getAddressKey(
    address: ShopperBasketsV2.schemas['OrderAddress'] | ShopperCustomers.schemas['CustomerAddress']
): string {
    return `${normalize(address.firstName)}-${normalize(address.lastName)}-${normalize(address.address1)}-${normalize(address.city)}-${normalize(address.stateCode)}-${normalize(address.postalCode)}-${normalize(address.countryCode)}`;
}

/**
 * Compares two addresses for equality by their normalized field values.
 */
export function isAddressEqual(
    address1?: ShopperBasketsV2.schemas['OrderAddress'] | null,
    address2?: ShopperBasketsV2.schemas['OrderAddress'] | null
): boolean {
    if (!address1 || !address2) return false;
    return getAddressKey(address1) === getAddressKey(address2);
}

/**
 * Converts an OrderAddress to a CustomerAddress format
 * This is useful for creating guest addresses or converting between address types
 *
 * @param orderAddress - The order address to convert
 * @param preferred - Whether this should be marked as a preferred address (defaults to false)
 * @returns CustomerAddress with auto-generated addressId
 */
export function orderAddressToCustomerAddress(
    orderAddress: ShopperBasketsV2.schemas['OrderAddress'],
    preferred: boolean = false
): ShopperCustomers.schemas['CustomerAddress'] {
    return {
        addressId: `shipping_${Date.now()}`, // Generate unique address ID
        address1: orderAddress.address1 || '',
        address2: orderAddress.address2,
        city: orderAddress.city || '',
        countryCode: orderAddress.countryCode || 'US',
        firstName: orderAddress.firstName || '',
        lastName: orderAddress.lastName || '',
        phone: orderAddress.phone,
        postalCode: orderAddress.postalCode || '',
        stateCode: orderAddress.stateCode,
        preferred,
    };
}

/**
 * Checks if an address has no meaningful content (all fields are empty/falsy)
 * Ignores the id field and only checks actual address fields
 * @param address - Address object to check
 * @returns true if address is empty or has no meaningful content
 */
export function isAddressEmpty(address: ShopperBasketsV2.schemas['OrderAddress'] | undefined | null): boolean {
    if (!address) return true;
    return (
        normalize(address.address1) === '' &&
        normalize(address.city) === '' &&
        normalize(address.countryCode) === '' &&
        normalize(address.firstName) === '' &&
        normalize(address.lastName) === '' &&
        normalize(address.phone) === '' &&
        normalize(address.postalCode) === '' &&
        normalize(address.stateCode) === ''
    );
}

/**
 * True when billing cannot satisfy SFCC order placement (e.g. phone-only stub set from contact step).
 * Used so checkout can copy a full shipping address onto billing when this returns true.
 */
export function isOrderBillingAddressIncomplete(
    address: ShopperBasketsV2.schemas['OrderAddress'] | undefined | null
): boolean {
    if (!address || isAddressEmpty(address)) {
        return true;
    }
    const address1 = normalize(address.address1);
    const city = normalize(address.city);
    const postalCode = normalize(address.postalCode);
    const countryCode = normalize(address.countryCode);
    return address1 === '' || city === '' || postalCode === '' || countryCode === '';
}

/**
 * True when a basket shipping address cannot be treated as a completed checkout
 * shipping destination.
 *
 * Stricter than {@link isAddressEmpty}: a postal-only stub (e.g. legacy hybrid
 * `validateShippingPostalCodeAgainstSessionZipCode` + `calculate` after a
 * deliver-to ZIP sync) is "not empty" for `isAddressEmpty` but must not render
 * as a completed Shipping Address step or summary card. Requires street, city,
 * and postal — the minimum the shopper entered on the shipping-address form.
 */
export function isShippingAddressIncomplete(
    address: ShopperBasketsV2.schemas['OrderAddress'] | undefined | null
): boolean {
    if (!address || isAddressEmpty(address)) {
        return true;
    }
    return normalize(address.address1) === '' || normalize(address.city) === '' || normalize(address.postalCode) === '';
}

type FormattedAddress = {
    /** Full name line (firstName + lastName). */
    nameLine: string;
    /** Street line (address1 + address2). */
    streetLine: string;
    /** City line (postalCode, city, stateCode, countryCode). */
    cityLine: string;
    /** Street and city joined for display (streetLine + cityLine). */
    addressLine: string;
    /** Single-line format for dropdowns: name, address1, city, stateCode, postalCode. */
    fullAddress: string;
};

/**
 * Formats an address for display. Returns structured lines and a single-line string.
 * @param address - The address to format
 * @param fallbackText - Used for fullAddress when address is null/undefined (defaults to empty string)
 * @returns Object with nameLine, streetLine, cityLine, and fullAddress (single-line for dropdowns)
 */
export function formatAddress(
    address?: ShopperBasketsV2.schemas['OrderAddress'] | ShopperCustomers.schemas['CustomerAddress'] | null,
    fallbackText: string = ''
): FormattedAddress {
    if (!address) {
        return { nameLine: '', streetLine: '', cityLine: '', addressLine: '', fullAddress: fallbackText };
    }
    const nameLine =
        address.firstName && address.lastName
            ? `${address.firstName} ${address.lastName}`
            : [address.firstName, address.lastName].filter(Boolean).join(' ') || '';
    const streetLine = [address.address1, address.address2].filter(Boolean).join(' ');
    const cityLine = [address.postalCode, address.city, address.stateCode, address.countryCode]
        .filter(Boolean)
        .join(', ');
    const fullAddressParts = [
        address.firstName && address.lastName ? `${address.firstName} ${address.lastName}` : null,
        address.address1,
        address.city && address.stateCode ? `${address.city}, ${address.stateCode}` : address.city || address.stateCode,
        address.postalCode,
    ].filter(Boolean);
    const fullAddress = fullAddressParts.join(', ');
    const addressLine = [streetLine, cityLine].filter(Boolean).join(', ');
    return { nameLine, streetLine, cityLine, addressLine, fullAddress };
}

/**
 * Converts a CustomerAddress to an OrderAddress format.
 * Uses the same structure as the shipping address submission body.
 * Applies default empty strings and 'US' for countryCode so the result is safe to spread into AddressBookItem.
 *
 * @param customerAddress - The customer address to convert
 * @returns OrderAddress without id (id should be added separately from addressId); all string fields normalized
 */
export function customerAddressToOrderAddress(
    customerAddress: ShopperCustomers.schemas['CustomerAddress']
): ShopperBasketsV2.schemas['OrderAddress'] {
    return {
        address1: customerAddress.address1 ?? '',
        address2: customerAddress.address2,
        city: customerAddress.city ?? '',
        countryCode: customerAddress.countryCode ?? 'US',
        firstName: customerAddress.firstName ?? '',
        lastName: customerAddress.lastName ?? '',
        phone: customerAddress.phone,
        postalCode: customerAddress.postalCode ?? '',
        stateCode: customerAddress.stateCode ?? '',
    };
}

/**
 * Finds the saved address that matches a basket/order shipping address by comparing
 * core address fields. Returns the matching address ID, or undefined if no match.
 */
export function findMatchingSavedAddressId(
    shippingAddress: ShopperBasketsV2.schemas['OrderAddress'] | undefined | null,
    savedAddresses: AddressBookItem[]
): string | undefined {
    if (!shippingAddress || savedAddresses.length === 0) return undefined;

    const key = getAddressKey(shippingAddress);
    const match = savedAddresses.find((saved) => getAddressKey(saved) === key);

    return match?.id;
}

/**
 * Converts an address book item to FormData for shipping address form submission.
 */
export function addressToFormData(address: AddressBookItem): FormData {
    const formData = new FormData();
    if (address.firstName) formData.append('firstName', address.firstName);
    if (address.lastName) formData.append('lastName', address.lastName);
    if (address.address1) formData.append('address1', address.address1);
    if (address.address2) formData.append('address2', address.address2);
    if (address.city) formData.append('city', address.city);
    if (address.stateCode) formData.append('stateCode', address.stateCode);
    if (address.postalCode) formData.append('postalCode', address.postalCode);
    if (address.countryCode) formData.append('countryCode', address.countryCode);
    if (address.phone) formData.append('phone', address.phone);
    return formData;
}
